import React, { FC } from 'react';

import { Box } from '@mui/material';

import BundleCourseHeader from './bundleCourseComponents/BundleCourseHeader';
import BundleCourseHighlightCover from './bundleCourseComponents/BundleCourseHighlightCover';
import BundleCourseHighlightCoverSkeleton from './bundleCourseComponents/skeleton/BundleCourseHighlightCoverSkeleton';
import BundleCourseInstructorSkeleton from './bundleCourseComponents/skeleton/BundleCourseInstructorSkeleton';

import Container from '@components/store/container';
import CourseCurriculum from '@components/store/product/courseCurriculum';
import CourseCurriculumSkeleton from '@components/store/product/courseCurriculum/courseCurriculumSkeleton';
import ProductCriteriaCertificate from '@components/store/product/productCriteriaCertificate';
import ProductCriteriaCertificateSkeleton from '@components/store/product/productCriteriaCertificate/productCriteriaCertificateSkeleton';
import ProductInstructor from '@components/store/product/productInstructor';
import CourseDescription from '@components/templates/courseDetail/courseDescription';
import { COURSE_REGULAR_PRESET_MENUS } from '@components/templates/courseDetail/courseDetailSection';
import useBundleCourse from '@hooks/bundle/useBundleCourse';
import { BundleEntity } from '@redux/bundle/type/BundleDetailType';
import BundleCourseDescription from './bundleCourseComponents/BundleCourseDescription';

type BundleCourseDetailProps = {
  bundleDetailSeo: BundleEntity;
};

const BundleCourseDetail: FC<BundleCourseDetailProps> = ({ bundleDetailSeo }) => {
  const { courseDetailSeo, courseDetail, isCourseDetailLoading, isCourseDetailSeoLoading } = useBundleCourse();

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 3, md: '36px', lg: 6 },
        }}
      >
        <Box px={{ xs: 3, md: '24px', lg: 0 }}>
          <Box
            sx={{
              width: '100%',
              height: { xs: '179px', md: '393px', lg: '512px' },
              borderRadius: '10px',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {isCourseDetailSeoLoading ? (
              <BundleCourseHighlightCoverSkeleton />
            ) : (
              <BundleCourseHighlightCover courseDetailSeo={courseDetailSeo} />
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box px={{ xs: 3, md: '24px', lg: 0 }}>
            <BundleCourseHeader bundleDetailSeo={bundleDetailSeo} />
          </Box>
          <Container
            containerSx={{
              display: 'grid',
              gridTemplateColumns: { lg: '1fr 30%' },
              gridTemplateRows: { lg: '40px 1fr' },
              gap: 2,
              pt: { xs: 3, md: 5, lg: 4.5 },
              pb: 0,
              px: { xs: 3, lg: 0 },
            }}
          >
            <BundleCourseDescription courseDetailSeo={courseDetailSeo} />
          </Container>

          <Container
            containerSx={{
              px: { xs: 3, lg: 0 },
              marginTop: { xs: 3, md: '36px' },
              display: 'grid',
              gridTemplateColumns: { lg: '1fr 30%' },
              gap: { xs: 5, lg: 2 },
            }}
          >
            {isCourseDetailLoading ? (
              <>
                <CourseCurriculumSkeleton />
                <BundleCourseInstructorSkeleton />
              </>
            ) : (
              <>
                <CourseCurriculum
                  scrollElementName={COURSE_REGULAR_PRESET_MENUS.CURRICULUM}
                  courseDetailSeo={courseDetailSeo}
                />
                <ProductInstructor
                  scrollElementName={COURSE_REGULAR_PRESET_MENUS.INSTRUCTOR}
                  instructors={courseDetailSeo?.instructors}
                />
              </>
            )}
          </Container>
          {isCourseDetailLoading ? (
            <ProductCriteriaCertificateSkeleton />
          ) : (
            courseDetail.hasCertificate && (
              <ProductCriteriaCertificate scrollElementName={COURSE_REGULAR_PRESET_MENUS.CERTIFICATE} />
            )
          )}
        </Box>
      </Box>
    </>
  );
};

export default BundleCourseDetail;
